/**
 * Das Zeichen „Der Gast hat geantwortet" auf Anfragen-Karte und -Zeile (Antoine, 19.10.2026:
 * „wie bei E-Mails"). Der Betreiber übersah Gast-Antworten auf ein Angebot, die Anfrage ging
 * verloren. Die Quelle ist `tatsachen.ungeleseneGastAntwortAm` (Server, `GET /api/inquiries`):
 * der Zeitpunkt der jüngsten ungelesenen, nicht automatischen Gast-Mail — `null` heißt, es gibt
 * nichts Ungelesenes.
 *
 * Zwei Bausteine, beide ohne Warnfarbe (Ruhige Hinweise: die Bedeutung trägt das Symbol, Rot ist
 * Fehlern vorbehalten) und trotzdem deutlich: ein gefüllter Punkt vor dem Namen wie der
 * Ungelesen-Punkt im Mailprogramm, und ein Chip mit Briefsymbol in der Lagezeile.
 */
import { Icon, ZEILEN_AKTION } from "./components";
import { timeAgo } from "./format";
import type { Inquiry } from "./types";

/** Zeitpunkt der ungelesenen Gast-Antwort, falls es eine gibt. */
export function ungeleseneGastAntwort(anfrage: Pick<Inquiry, "tatsachen">): string | null {
  return anfrage.tatsachen?.ungeleseneGastAntwortAm ?? null;
}

/** Gefüllter Punkt vor dem Kundennamen (wie der Ungelesen-Punkt im Mailprogramm). */
export function UngelesenPunkt() {
  return (
    <span
      data-testid="karte-ungelesen-punkt"
      role="img"
      aria-label="Ungelesene Antwort vom Gast"
      title="Ungelesene Antwort vom Gast"
      className="mr-1.5 inline-block h-2 w-2 shrink-0 rounded-full bg-on-background align-middle dark:bg-inverse-on-surface"
    />
  );
}

/**
 * Chip „Antwort vom Gast" mit Briefsymbol. Mit `onKlick` ein Knopf, der die Antwort-Ansicht
 * öffnet (liegt über dem Zeilen-Link); ohne bleibt er eine reine Auskunft.
 */
export function GastAntwortChip({ am, onKlick }: { am: string; onKlick?: () => void }) {
  const wann = timeAgo(am);
  const inhalt = (
    <>
      <Icon name="mark_email_unread" className="shrink-0 text-[14px]" />
      Antwort vom Gast
      {wann !== "–" && <span className="font-normal text-on-surface-variant/80">· {wann.toLowerCase()}</span>}
    </>
  );
  const klassen =
    "flex items-center gap-1 whitespace-nowrap font-body-sm text-[12px] font-semibold text-on-background dark:text-inverse-on-surface";
  if (!onKlick) {
    return (
      <span data-testid="karte-gast-antwort" className={klassen}>
        {inhalt}
      </span>
    );
  }
  return (
    <button
      type="button"
      data-testid="karte-gast-antwort"
      onClick={(e) => {
        e.stopPropagation();
        onKlick();
      }}
      title="Per E-Mail antworten"
      className={`${klassen} ${ZEILEN_AKTION} rounded-md hover:underline`}
    >
      {inhalt}
    </button>
  );
}
